/**
 * Layout of the questionnaire editor: the redesign with palette and property panel ('new') or the single column
 * editor of the old administration ('classic'), where every card shows its properties itself. Both layouts share
 * the same components, the classic one only adds the old card frame around the shared property template.
 * The instance default comes from the config (ui.editor.default), a browser can override it for testing.
 */
export type EditorLayout = 'new' | 'classic';

const STORAGE_KEY = 'qa-editor-layout';

export function getEditorLayout(configDefault?: string): EditorLayout {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'classic' || stored === 'new') {
      return stored;
    }
  } catch (e) {}
  return configDefault === 'classic' ? 'classic' : 'new';
}

/** Overrides the layout for this browser, null goes back to the instance default */
export function setEditorLayout(layout: EditorLayout | null): void {
  try {
    layout ? localStorage.setItem(STORAGE_KEY, layout) : localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
}

/**
 * Classic layout: a card renders its property panel once it comes near the viewport. Hundreds of full panels made
 * loading a questionnaire and switching the page view take seconds. One shared observer serves all cards
 */
let nearObserver: IntersectionObserver = null;
const nearCallbacks = new Map<Element, () => void>();

export function renderWhenNear(element: Element, render: () => void): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    render();
    return () => {};
  }
  if (!nearObserver) {
    nearObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      const callback = entry.isIntersecting && nearCallbacks.get(entry.target);
      if (callback) {
        nearCallbacks.delete(entry.target);
        nearObserver.unobserve(entry.target);
        callback();
      }
    }), {rootMargin: '1500px 0px'});
  }
  nearCallbacks.set(element, render);
  nearObserver.observe(element);
  return () => {
    nearCallbacks.delete(element);
    nearObserver.unobserve(element);
  };
}
